import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Tender CRUD page in Non-Spot mode.
 */
const NON_SPOT_TENDER_CRUD_ROUTE = "tenders/TenderConfigCrud";

/**
 * Fixed filter used to show only Non-Spot Tenders.
 */
const NON_SPOT_TENDER_FILTER = "TenderTypeId eq 2";

//endregion

//region Component

/**
 * Renders the Non-Spot Tenders configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep the page visually aligned with other Config tabs through config-page.css;
 * - render the Tenders MTable filtered by Non-Spot Tender type;
 * - use the Tenders permission module;
 * - disable Create/Copy/Print actions for this workflow;
 * - replace default View/Edit table actions with tab-based navigation;
 * - persist the selected Tender row before opening View/Edit tabs.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Non-Spot Tenders configuration tab.
 */
export default function NonSpotTenderConfig({
    roles,
    onCreateTab,
    toastMessages,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this tab.
     *
     * Important:
     * - Non-Spot Tender creation is not exposed here;
     * - Copy is disabled to keep tender creation explicit;
     * - Print is not part of the desired workflow.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Create"],
        []
    );

    /**
     * Opens the Non-Spot Tender CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected Tender row as TenderLine when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected Tender row.
     * @returns {void}
     */
    const openNonSpotTenderCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "TenderLine",
                        value: row,
                    })
                );
            }

            const safeRequestForQuotes = row?.RequestForQuotes ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewTender")
                    : operation === "View"
                        ? `${t("LabelViewTender")} "${safeRequestForQuotes}"`
                        : `${t("LabelEditTender")} "${safeRequestForQuotes}"`;

            onCreateTab(NON_SPOT_TENDER_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Non-Spot Tender workflow.
     *
     * Important:
     * - Create is intentionally not exposed in this tab;
     * - View/Edit use the unified TenderConfigCrud route.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openNonSpotTenderCrud("View", row)],
                ["Edit", (row) => openNonSpotTenderCrud("Edit", row)],
            ]),
        [openNonSpotTenderCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("NonSpotTenders")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoNonSpotTenders")}
                </div>
            </div>

            <div className="config-page__table config-page__table--tabs">
                <MTable
                    filtersOnTable
                    entityName="Tenders"
                    roleName="Tenders"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    FILTER={NON_SPOT_TENDER_FILTER}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

NonSpotTenderConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion